import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  FormControlLabel, Switch,
} from '@mui/material'
import { toast } from 'react-toastify'
import {
  getMargenMatriz, getMarcas, getClientes, getMargenes, guardarMargen, eliminarMargen,
} from 'src/services/preciosService'
import { fmt, pct, chip, PuntoColor } from './columnasPrecios'

/**
 * La hoja PRECIOS del Excel, viva.
 *
 * Una fila por moto y una columna por lista/cliente — Agente %, CENCOSUD, Leticia San Andrés%,
 * Base Concesionario%, Concesionario Margen 12%, G20 Margen 10%, los ZDPC — más los conceptos
 * (+ Fondo Movilidad, + Chaleco, Aux Flete). Es la pantalla que faltaba para contestar dos preguntas
 * que hoy solo se pueden mirar en el Excel:
 *
 *   ¿cuánto gana cada cliente con ESTA moto?   -> se lee una fila
 *   ¿qué margen tiene ESTE cliente en todas?   -> se elige el cliente arriba
 *
 * Cada celda dice además DE DÓNDE sale el margen: la regla global, la de la marca, la excepción de ese
 * modelo o una negociación de ese cliente. Eso es lo que hoy nadie puede auditar.
 */

/** Columnas que el Excel trae pero ya no se usan. Se muestran para que no parezca que faltan. */
const MUERTAS = [
  ['FAM %', 'Ya no se usa.'],
  ['Mi moto Auteco%', 'Ya no se usa.'],
  ['Mi Moto Otros%', 'Ya no se usa.'],
]

const ORIGENES = {
  cliente_modelo: { bg: '#f3e8fd', color: '#6a1b9a', txt: 'Negociación del cliente para esta moto' },
  cliente_marca: { bg: '#f3e8fd', color: '#6a1b9a', txt: 'Negociación del cliente para la marca' },
  cliente: { bg: '#efe4fb', color: '#5b1687', txt: 'Margen negociado del cliente' },
  modelo: { bg: '#fff1d6', color: '#8a5a00', txt: 'Margen de esta moto' },
  marca: { bg: '#e4eefb', color: '#14508f', txt: 'Regla de la marca' },
  global: { bg: '#ffffff', color: '#2b3440', txt: 'Regla general de la lista' },
  // f51: la clasificación no tiene margen propio y toma el de aquella de la que hereda.
  heredado: { bg: '#eef0f2', color: '#56606b', txt: 'Heredado de otra clasificación' },
  sin_regla: { bg: '#fdecec', color: '#b42318', txt: 'Sin margen cargado — sale en 0 hasta que se ponga' },
}

/**
 * Alcances con los que se puede guardar un margen desde acá, del más específico al más general.
 *
 * NO está la regla GENERAL de la lista a propósito. Ese dato se administra en «Clasificación de
 * clientes», que lo guarda VERSIONADO (cierra la vigencia anterior e inserta la nueva). Esta pantalla
 * escribe por otro camino que sobrescribe sin dejar historia, así que tenerlo en los dos lados era
 * tener dos verdades para el mismo número. Acá quedan solo las EXCEPCIONES a esa regla general.
 */
const ALCANCES = [
  { key: 'cliente_modelo', label: 'Solo este cliente en esta moto' },
  { key: 'cliente_marca', label: 'Solo este cliente en toda la marca' },
  { key: 'cliente', label: 'Este cliente en todas las motos' },
  { key: 'modelo', label: 'Esta moto (modelo + año), para todos' },
  { key: 'marca', label: 'Toda la marca, para todos' },
]

const DONDE_SE_EDITA = 'Sin margen, la moto sale en 0 y su plano no se arma hasta cargarlo.'

const MargenesTab = () => {
  const [filas, setFilas] = useState([])
  const [marcas, setMarcas] = useState([])
  const [clientes, setClientes] = useState([])
  const [reglas, setReglas] = useState([])
  const [marca, setMarca] = useState('')
  const [q, setQ] = useState('')
  const [cliente, setCliente] = useState(null)
  const [ver, setVer] = useState('margen')
  const [isLoading, setIsLoading] = useState(false)
  const [edit, setEdit] = useState(null)
  // Edición directa desde la tabla de reglas: el ámbito ya está fijado por la regla, solo cambia el valor.
  // La matriz trae 60 motos y casi todas usan el margen general. Este filtro deja solo las que se salen.
  const [soloDesviadas, setSoloDesviadas] = useState(false)


  const buscar = async (over = {}) => {
    setIsLoading(true)
    const r = await getMargenMatriz({
      marca: over.marca !== undefined ? over.marca : marca,
      q: over.q !== undefined ? over.q : q,
      vendedorId: over.vendedorId !== undefined ? over.vendedorId : (cliente ? cliente.vendedor_id : 0),
      limite: 60,
    })
    setFilas((r && r.data) || [])
    setIsLoading(false)
  }

  const cargarReglas = async () => {
    const r = await getMargenes(0)
    setReglas((r && r.data) || [])
  }

  useEffect(() => {
    cargarReglas()
    getMarcas().then((r) => setMarcas(((r && r.data) || []).map((x) => x.marca)))
    getClientes('').then((r) => {
      // el select trae una fila por cliente x condición: acá se necesita el cliente una sola vez
      const vistos = new Map()
      ;((r && r.data) || []).forEach((x) => {
        if (!vistos.has(x.vendedor_id)) vistos.set(x.vendedor_id, x)
      })
      setClientes([...vistos.values()])
    })
    buscar({ marca: '', q: '', vendedorId: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Pivot: de (moto x lista) a una fila por moto con una celda por lista. */
  const { motos, listas } = useMemo(() => {
    const porMoto = new Map()
    const cols = new Map()
    filas.forEach((f) => {
      if (!cols.has(f.lista_id)) {
        cols.set(f.lista_id, {
          id: f.lista_id,
          label: f.lista_label,
          tabla: f.tabla_sap,
          condicion: f.condicion,
          formula: f.formula,
          orden: Number(f.orden || 0),
        })
      }
      if (!porMoto.has(f.grupo_id)) {
        porMoto.set(f.grupo_id, {
          grupo_id: f.grupo_id,
          conca: f.conca,
          marca: f.marca,
          modelo: f.modelo,
          anio_modelo: f.anio_modelo,
          zona: f.zona,
          materiales: f.materiales,
          materiales_texto: f.materiales_texto,
          colores_texto: f.colores_texto,
          pvp: Number(f.pvp || 0),
          pvp_vigente: Number(f.pvp_vigente || 0),
          pvp_propuesto: Number(f.pvp_propuesto || 0),
          iva_pct: f.iva_pct,
          pv_sin_impuestos: Number(f.pv_sin_impuestos || 0),
          aux_flete: Number(f.aux_flete || 0),
          fondo_movilidad: Number(f.fondo_movilidad || 0),
          chaleco: Number(f.chaleco || 0),
          celdas: {},
        })
      }
      porMoto.get(f.grupo_id).celdas[f.lista_id] = f
    })
    return {
      motos: [...porMoto.values()],
      listas: [...cols.values()].sort((a, b) => a.orden - b.orden),
    }
  }, [filas])

  /** Una moto "se desvía" si al menos una de sus celdas no usa el margen de tabla de esa lista. */
  const seDesvia = (m) => Object.values(m.celdas).some(
    (c) => c && Number(c.aplica) !== 0 && Number(c.margen) !== Number(c.margen_base),
  )
  const motosVista = useMemo(
    () => (soloDesviadas ? motos.filter(seDesvia) : motos),
    [motos, soloDesviadas],
  )
  const nDesviadas = useMemo(() => motos.filter(seDesvia).length, [motos])

  /**
   * Busca si YA existe una regla guardada para ese alcance exacto. Sirve para dos cosas: precargar el id
   * (así se edita en vez de duplicar) y poder ofrecer "quitar la excepción".
   */
  const reglaDe = (listaMargenId, alcance, m) =>
    reglas.find((r) => {
      if (Number(r.lista_id) !== Number(listaMargenId)) return false
      const conCliente = alcance.startsWith('cliente')
      const vend = Number(r.vendedor_id || 0)
      if (conCliente && (!cliente || vend !== Number(cliente.vendedor_id))) return false
      if (!conCliente && vend !== 0) return false
      const porModelo = alcance.endsWith('modelo')
      const porMarca = alcance.endsWith('marca')
      if (porModelo) return r.modelo === m.modelo && Number(r.anio_modelo || 0) === Number(m.anio_modelo || 0)
      if (porMarca) return r.marca === m.marca && !r.modelo
      if (alcance === 'cliente') return !r.marca && !r.modelo
      return !r.marca && !r.modelo // global
    })

  const abrirEdicion = (m, l) => {
    const c = m.celdas[l.id]
    if (!c) return
    if (c.formula === 'base' || c.formula === 'pvp' || c.formula === 'fijo') {
      toast.info(`${l.label} no lleva margen: ${c.formula === 'pvp' ? 'es el PVP tal cual' : 'es una base'}.`)
      return
    }
    // por defecto se propone lo más específico razonable: la moto (o el cliente en la moto)
    const alcance = cliente ? 'cliente_modelo' : 'modelo'
    // Se guarda contra ESTA clasificación (`l.id`), no contra la que le presta el margen
    // (`c.lista_margen_id`). Escribir en la de arriba movía de una a todas sus hermanas, que es
    // justo lo que pidió el comercial que dejara de pasar. Ver precios_f51.
    const existente = reglaDe(l.id, alcance, m)
    setEdit({
      moto: m,
      lista: l,
      listaMargenId: l.id,
      heredaDe: c.hereda_de_label || '',
      alcance,
      valor: String((Number(c.margen || 0) * 100).toFixed(2).replace(/\.?0+$/, '')),
      nota: '',
      id: existente ? existente.id : 0,
      actual: c,
    })
  }

  const cambiarAlcance = (alcance) => {
    const existente = reglaDe(edit.listaMargenId, alcance, edit.moto)
    setEdit((prev) => ({
      ...prev,
      alcance,
      id: existente ? existente.id : 0,
      valor: existente
        ? String((Number(existente.valor) * 100).toFixed(2).replace(/\.?0+$/, ''))
        : prev.valor,
    }))
  }

  const guardar = async () => {
    const v = Number(String(edit.valor).replace(',', '.'))
    if (Number.isNaN(v) || v < 0 || v >= 100) {
      toast.warn('El margen va entre 0 y 99,99 %.')
      return
    }
    const conCliente = edit.alcance.startsWith('cliente')
    const porModelo = edit.alcance.endsWith('modelo')
    const porMarca = edit.alcance.endsWith('marca')
    const r = await guardarMargen({
      id: String(edit.id || 0),
      lista_id: String(edit.listaMargenId),
      vendedor_id: conCliente && cliente ? String(cliente.vendedor_id) : '0',
      marca: porModelo || porMarca ? (edit.moto.marca || '') : '',
      modelo: porModelo ? edit.moto.modelo : '',
      anio_modelo: porModelo ? String(edit.moto.anio_modelo || 0) : '0',
      zona: '',
      valor: String(v / 100),
      nota: edit.nota || `Editado desde la matriz · ${edit.lista.label}`,
    })
    const d = (r && r.data && r.data[0]) || null
    if (!d || Number(d.result) === 0) {
      toast.error((d && d.error) || 'No se pudo guardar el margen.')
      return
    }
    toast.success(`${edit.lista.label}: margen ${v}% guardado.`)
    setEdit(null)
    await cargarReglas()
    buscar()
  }

  const quitar = async () => {
    if (!edit.id) return
    const r = await eliminarMargen(edit.id)
    if (!r) {
      toast.error('No se pudo quitar la excepción.')
      return
    }
    toast.success('Excepción quitada: vuelve a mandar la regla más general.')
    setEdit(null)
    await cargarReglas()
    buscar()
  }

  const th = (txt, extra = {}) => (
    <th
      style={{
        padding: '7px 9px',
        fontSize: 10.5,
        fontWeight: 700,
        color: '#56606b',
        textTransform: 'uppercase',
        letterSpacing: '.4px',
        background: '#f4f6f8',
        borderBottom: '1px solid #e3e7ec',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        ...extra,
      }}
    >
      {txt}
    </th>
  )

  const tdBase = {
    padding: '6px 9px',
    fontSize: 12,
    color: '#2b3440',
    borderBottom: '1px solid #eef1f4',
    whiteSpace: 'nowrap',
  }

  const celda = (m, l) => {
    const c = m.celdas[l.id]
    if (!c) {
      return (
        <td key={l.id} style={{ ...tdBase, background: '#fafbfc', textAlign: 'right', color: '#c9ced4' }}>
          —
        </td>
      )
    }
    const noAplica = Number(c.aplica) === 0
    const esPct = l.condicion === 'ZDPC'
    const sinMargen = c.formula === 'base' || c.formula === 'pvp' || c.formula === 'fijo'
    const o = ORIGENES[c.origen] || ORIGENES.global
    const desviado = Number(c.margen) !== Number(c.margen_base)

    if (noAplica) {
      return (
        <td key={l.id} style={{ ...tdBase, background: '#fafbfc', color: '#9aa5b1', textAlign: 'right' }}>
          <span title="Esta moto no está marcada en ese grupo de descuento">no aplica</span>
        </td>
      )
    }

    return (
      <td
        key={l.id}
        role="button"
        tabIndex={0}
        onClick={() => abrirEdicion(m, l)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') abrirEdicion(m, l)
        }}
        style={{
          ...tdBase,
          background: sinMargen ? '#fff' : o.bg,
          textAlign: 'right',
          borderLeft: desviado ? '2px solid #e0a300' : '2px solid transparent',
          cursor: sinMargen ? 'default' : 'pointer',
        }}
        title={`${l.label}\n${sinMargen ? 'No lleva margen' : `${o.txt} · margen ${pct(c.margen)}`}${
          desviado ? `\nSe desvía del margen general (${pct(c.margen_base)})` : ''
        }${sinMargen ? '' : '\n\nClic para editar el %'}`}
      >
        {ver === 'margen' && !sinMargen && (
          <span style={{ fontWeight: 700, color: o.color }}>{pct(c.margen)}</span>
        )}
        {ver === 'margen' && sinMargen && <span style={{ color: '#9aa5b1' }}>—</span>}
        {ver === 'precio' && (
          esPct
            ? <span style={{ fontWeight: 700, color: o.color }}>{pct(c.margen)}</span>
            : <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.valor)}</span>
        )}
        {ver === 'margen' && !sinMargen && (
          <div style={{ fontSize: 10.5, color: '#8a94a0', fontVariantNumeric: 'tabular-nums' }}>
            {esPct ? '% descuento' : fmt(c.valor)}
          </div>
        )}
      </td>
    )
  }

  return (
    <div>
      <div className="precios-card">
        <div className="precios-section-title">Márgenes por moto y por cliente</div>
        <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 10 }}>
          Una fila por moto, una columna por lista. El <b>margen es lo que gana el cliente</b>; cada
          celda dice de dónde sale.
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <TextField
            select
            size="small"
            label="Marca"
            value={marca}
            onChange={(e) => {
              setMarca(e.target.value)
              buscar({ marca: e.target.value })
            }}
            SelectProps={{ native: true }}
            sx={{ minWidth: 170 }}
          >
            <option value="">Todas</option>
            {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
          </TextField>
          <TextField
            size="small"
            label="Buscar modelo"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') buscar()
            }}
            sx={{ minWidth: 220 }}
          />
          <Autocomplete
            options={clientes}
            value={cliente}
            onChange={(e, v) => {
              setCliente(v)
              buscar({ vendedorId: v ? v.vendedor_id : 0 })
            }}
            getOptionLabel={(o) => o.nombre || ''}
            isOptionEqualToValue={(a, b) => a.vendedor_id === b.vendedor_id}
            size="small"
            sx={{ minWidth: 260 }}
            renderInput={(params) => (
              <TextField {...params} label="Ver como un cliente" placeholder="Todos (margen general)" />
            )}
          />
          <TextField
            select
            size="small"
            label="Mostrar"
            value={ver}
            onChange={(e) => setVer(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 190 }}
          >
            <option value="margen">Margen %</option>
            <option value="precio">Precio facturado</option>
          </TextField>
          <Button variant="contained" size="small" onClick={() => buscar()}>
            Buscar
          </Button>
          {/* La mayoría de las motos usa el margen de su clasificación; lo que hay que administrar
              son las que NO. Sin este filtro se revisan 60 filas para encontrar unas pocas. */}
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 12.5 } }}
            control={
              <Switch
                size="small"
                checked={soloDesviadas}
                onChange={(e) => setSoloDesviadas(e.target.checked)}
              />
            }
            label={`Solo las que se desvían (${nDesviadas} de ${motos.length})`}
          />
        </div>

        {/* Leyenda: sin esto los colores no dicen nada */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {['global', 'marca', 'modelo', 'cliente_modelo'].map((k) => (
            <span
              key={k}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11.5,
                color: '#6b7684',
                border: '1px solid #e3e7ec',
                borderRadius: 20,
                padding: '2px 10px',
              }}
            >
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: 3,
                  background: ORIGENES[k].bg,
                  border: '1px solid #d9dfe5',
                }}
              />
              {ORIGENES[k].txt}
            </span>
          ))}
          <span style={{ fontSize: 11.5, color: '#6b7684' }}>
            <span style={{ borderLeft: '3px solid #e0a300', paddingLeft: 5 }}>
              barra amarilla = se desvía del margen general
            </span>
          </span>
        </div>

        {cliente && (
          <div
            style={{
              background: '#f3e8fd',
              border: '1px solid #ddc6f5',
              color: '#5b1687',
              borderRadius: 8,
              padding: '9px 12px',
              fontSize: 12.5,
              marginBottom: 10,
            }}
          >
            Viendo como <b>{cliente.nombre}</b>. Morado con barra amarilla = su negociación propia.
          </div>
        )}

        <div style={{ overflowX: 'auto', border: '1px solid #e3e7ec', borderRadius: 10 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1200 }}>
            <thead>
              <tr>
                {th('Material')}
                {th('Marca')}
                {th('Modelo')}
                {th('Año')}
                {th('Colores')}
                {th('PVP', { textAlign: 'right' })}
                {th('IVA', { textAlign: 'right' })}
                {th('PV sin imp.', { textAlign: 'right' })}
                {listas.map((l) => (
                  <th
                    key={l.id}
                    title={`${l.tabla} · ${l.condicion}`}
                    style={{
                      padding: '7px 9px',
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: '#56606b',
                      background: '#f4f6f8',
                      borderBottom: '1px solid #e3e7ec',
                      borderLeft: '1px solid #e9edf1',
                      textAlign: 'right',
                      minWidth: 118,
                    }}
                  >
                    {l.label}
                    <div style={{ fontWeight: 500, color: '#8a94a0', textTransform: 'none' }}>
                      {l.tabla} · {l.condicion}
                    </div>
                  </th>
                ))}
                {th('(+) Fondo Movilidad', { textAlign: 'right' })}
                {th('(+) Chaleco', { textAlign: 'right' })}
                {th('Aux Flete', { textAlign: 'right' })}
                {MUERTAS.map(([n, why]) => (
                  <th
                    key={n}
                    title={why}
                    style={{
                      padding: '7px 9px',
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: '#b3bac1',
                      background: '#f7f8fa',
                      borderBottom: '1px solid #e3e7ec',
                      textAlign: 'right',
                      textDecoration: 'line-through',
                      minWidth: 100,
                    }}
                  >
                    {n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {motosVista.map((m, i) => (
                <tr key={m.grupo_id} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
                  <td style={{ ...tdBase, fontVariantNumeric: 'tabular-nums' }} title={m.materiales_texto}>
                    {(m.materiales_texto || '').split(', ')[0] || '—'}
                    {m.materiales > 1 && <span style={{ color: '#6b7684' }}> +{m.materiales - 1}</span>}
                  </td>
                  <td style={tdBase}>{m.marca || <span style={{ color: '#9aa5b1' }}>—</span>}</td>
                  <td style={{ ...tdBase, fontWeight: 600 }}>{m.modelo}</td>
                  <td style={tdBase}>{m.anio_modelo || '—'}</td>
                  <td style={tdBase} title={m.colores_texto || ''}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {(m.colores_texto || '').split(', ').filter(Boolean).slice(0, 3)
                        .map((c) => <PuntoColor key={c} nombre={c} />)}
                      {m.materiales}
                    </span>
                  </td>
                  <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {m.pvp > 0 ? fmt(m.pvp) : <span style={{ color: '#d64545' }}>sin precio</span>}
                    {m.pvp_propuesto > 0 && m.pvp_propuesto !== m.pvp_vigente && (
                      <div style={{ fontSize: 10.5, color: '#b26a00' }}>propuesto, sin publicar</div>
                    )}
                  </td>
                  <td style={{ ...tdBase, textAlign: 'right' }}>{pct(m.iva_pct)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#1f9d55' }}>
                    {fmt(m.pv_sin_impuestos)}
                  </td>
                  {listas.map((l) => celda(m, l))}
                  <td style={{ ...tdBase, textAlign: 'right', color: '#6b7684' }}>{fmt(m.fondo_movilidad)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', color: '#6b7684' }}>{fmt(m.chaleco)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', color: '#6b7684' }}>{fmt(m.aux_flete)}</td>
                  {MUERTAS.map(([n]) => (
                    <td key={n} style={{ ...tdBase, textAlign: 'right', color: '#c9ced4', background: '#fcfcfd' }}>
                      —
                    </td>
                  ))}
                </tr>
              ))}
              {!motosVista.length && (
                <tr>
                  <td colSpan={11 + listas.length} style={{ ...tdBase, textAlign: 'center', color: '#9aa5b1', padding: 22 }}>
                    {isLoading
                      ? 'Consultando…'
                      : soloDesviadas && motos.length
                        ? 'Ninguna de estas motos se desvía: todas usan el margen de su clasificación.'
                        : 'No hay modelos con ese filtro.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: 11.5, color: '#9aa5b1', marginTop: 8 }}>
          <b>Clic en una celda para cambiar el %.</b> Máximo 60 modelos; si falta uno, búscalo por modelo o material.
        </div>
      </div>

      {/* Editor del % */}
      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        {edit && (
          <>
            <DialogTitle>
              Margen de {edit.lista.label}
              <div style={{ fontSize: 12.5, color: '#6b7684', fontWeight: 400 }}>
                {edit.moto.marca} {edit.moto.modelo} {edit.moto.anio_modelo} · {edit.moto.materiales} color(es)
              </div>
            </DialogTitle>
            <DialogContent>
              <div
                style={{
                  background: '#f7f8fa',
                  border: '1px solid #e3e7ec',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 12.5,
                  color: '#2b3440',
                  marginTop: 4,
                }}
              >
                Hoy resuelve <b>{pct(edit.actual.margen)}</b> —{' '}
                {(ORIGENES[edit.actual.origen] || ORIGENES.global).txt.toLowerCase()}. Con ese margen la moto
                se factura en <b>{fmt(edit.actual.valor)}</b>.
                {edit.heredaDe && (
                  <div style={{ marginTop: 6, color: '#8a5a00' }}>
                    Hoy <b>hereda</b> de <b>{edit.heredaDe}</b>. Al guardar tendrá margen <b>propio</b>;{' '}
                    <b>{edit.heredaDe}</b> no cambia.
                  </div>
                )}
              </div>

              <TextField
                select
                fullWidth
                size="small"
                label="¿A qué le aplica este margen?"
                value={edit.alcance}
                onChange={(e) => cambiarAlcance(e.target.value)}
                sx={{ mt: 2 }}
              >
                {ALCANCES.filter((a) => (a.key.startsWith('cliente') ? Boolean(cliente) : true)).map((a) => (
                  <MenuItem key={a.key} value={a.key}>
                    {a.key.startsWith('cliente') && cliente
                      ? a.label.replace('este cliente', cliente.nombre).replace('Este cliente', cliente.nombre)
                      : a.label}
                    {a.key === 'modelo' && ` (${edit.moto.modelo} ${edit.moto.anio_modelo})`}
                    {a.key === 'marca' && ` (${edit.moto.marca || 'sin marca'})`}
                  </MenuItem>
                ))}
              </TextField>
              {!cliente && (
                <div style={{ fontSize: 11.5, color: '#9aa5b1', marginTop: 4 }}>
                  Para un margen negociado de un cliente, elige primero el cliente arriba.
                </div>
              )}
              <div style={{ fontSize: 11.5, color: '#6b7684', marginTop: 6 }}>
                Se guarda para el <b>alcance elegido</b>. {DONDE_SE_EDITA}
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 14, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  label="Margen %"
                  value={edit.valor}
                  onChange={(e) => setEdit({ ...edit, valor: e.target.value })}
                  sx={{ width: 130 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Nota (por qué)"
                  placeholder="Ej: acta del comité de julio"
                  value={edit.nota}
                  onChange={(e) => setEdit({ ...edit, nota: e.target.value })}
                />
              </div>

              {edit.id > 0 && (
                <div style={{ marginTop: 10 }}>
                  {chip('Ya existe una regla en ese alcance: se actualiza', '#eaf2fb', '#1c4a80')}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {edit.id > 0 && (
                <Button color="error" onClick={quitar}>Quitar la excepción</Button>
              )}
              <Button onClick={() => setEdit(null)}>Cancelar</Button>
              <Button variant="contained" onClick={guardar}>Guardar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

    </div>
  )
}

export default MargenesTab
